import { useState, useEffect, type FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { ChevronLeft, User, Mail, CreditCard, CheckCircle, XCircle, Loader2, AlertCircle } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { SearchablePicklist } from "@/components/ui/searchable-picklist"
import { useAuth } from "@/context/AuthContext"
import api from "@/services/api"

interface ConflictRecord {
    id: number
    first_name: string
    last_name: string
    email: string
    passport_number: string
}

interface FormData {
    first_name: string
    last_name: string
    citizenship: string
    passport_number: string
    applicant_email: string
}

const emptyForm: FormData = {
    first_name: "",
    last_name: "",
    citizenship: "",
    passport_number: "",
    applicant_email: "",
}

export default function AgencyApplicantCreatePage() {
    const navigate = useNavigate()
    const { user } = useAuth()

    const [agencyStatus, setAgencyStatus] = useState<string | null>(null)
    const [statusLoading, setStatusLoading] = useState(true)
    const [step, setStep] = useState<1 | 2>(1)
    const [form, setForm] = useState<FormData>(emptyForm)
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormData, string>>>({})
    const [countries, setCountries] = useState<string[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [createdMemberId, setCreatedMemberId] = useState<number | null>(null)
    const [conflicts, setConflicts] = useState<ConflictRecord[]>([])
    const [selectedConflictId, setSelectedConflictId] = useState<number | null>(null)

    useEffect(() => {
        api.get("/agency/status/")
            .then((res) => setAgencyStatus(res.data.status))
            .catch(() => setAgencyStatus("Passive"))
            .finally(() => setStatusLoading(false))
    }, [])

    useEffect(() => {
        api.get("/integrations/hub/countries/")
            .then((res) => {
                if (res.data.status === "success") setCountries(res.data.countries || [])
            })
            .catch(() => {})
    }, [])

    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    const handleChange = (field: keyof FormData, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
        setError(null)
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setError(null)

        const newErrors: Partial<Record<keyof FormData, string>> = {}
        if (!form.first_name.trim()) newErrors.first_name = "First name is required."
        if (!form.last_name.trim()) newErrors.last_name = "Last name is required."
        if (!form.citizenship) newErrors.citizenship = "Please select a citizenship."
        if (!form.passport_number.trim()) newErrors.passport_number = "Passport number is required."
        if (!form.applicant_email.trim()) {
            newErrors.applicant_email = "Email is required."
        } else if (!EMAIL_REGEX.test(form.applicant_email)) {
            newErrors.applicant_email = "Please enter a valid email address."
        }

        if (Object.keys(newErrors).length > 0) {
            setFieldErrors(newErrors)
            return
        }

        setIsSubmitting(true)
        try {
            const res = await api.post("/agency/applicants/create/", {
                ...form,
                institution_id: user?.institution_id,
            })
            setCreatedMemberId(res.data.member_id)
            setConflicts([])
            setSelectedConflictId(null)
            setStep(2)
        } catch (err: any) {
            const code = err.response?.data?.error
            if (code === "duplicate_found") {
                const raw = err.response.data.conflicts as Record<string, ConflictRecord>
                setConflicts(Object.values(raw))
                setStep(2)
                return
            }
            const msgMap: Record<string, string> = {
                name_required: "First and last name are required.",
                citizenship_required: "Citizenship is required.",
                passport_number_required: "Passport number is required.",
                email_required: "Email is required.",
                institution_required: "No institution is linked to your agency.",
                institution_not_found: "Linked institution not found.",
                forbidden: "You do not have permission to perform this action.",
            }
            setError(msgMap[code] || "An error occurred. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleGoToStudent = () => {
        const targetId = conflicts.length > 0 ? selectedConflictId : createdMemberId
        if (!targetId) {
            setError("Please select a record to continue.")
            return
        }
        navigate(`/partner/students/${targetId}`)
    }

    const handleBack = () => {
        setStep(1)
        setConflicts([])
        setSelectedConflictId(null)
        setCreatedMemberId(null)
        setError(null)
        setFieldErrors({})
    }

    if (statusLoading) {
        return (
            <div className="flex flex-col gap-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 animate-pulse rounded-2xl border border-slate-100 bg-white" />
                ))}
            </div>
        )
    }

    if (agencyStatus !== "Agreed") {
        return (
            <div className="flex items-center justify-center min-h-[320px]">
                <div className="border border-slate-100 shadow-sm rounded-2xl bg-white p-10 flex flex-col items-center gap-4 text-center max-w-md">
                    <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center">
                        <AlertCircle className="h-7 w-7 text-amber-500" />
                    </div>
                    <p className="text-base font-bold text-slate-800">Your agreement is not active yet.</p>
                </div>
            </div>
        )
    }

    return (
        <div className="w-full mx-auto pb-10" style={{ maxWidth: "80%" }}>
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate("/partner/home")}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                </button>
                <h1 className="text-lg font-bold text-slate-800">New Applicant</h1>
            </div>

            {step === 1 && (
                <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
                    <div className="mb-6">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Step 1 of 2</p>
                        <h2 className="text-base font-bold text-slate-800">Applicant Information</h2>
                        {user?.institution_name && (
                            <p className="text-xs text-slate-400 mt-1">{user.institution_name}</p>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-start gap-3 p-3 mb-5 bg-red-50 text-red-700 rounded-xl border border-red-100">
                            <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    First Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="text"
                                        value={form.first_name}
                                        onChange={(e) => handleChange("first_name", e.target.value)}
                                        placeholder="First name"
                                        className={`pl-9 pr-3 py-3 h-auto rounded-xl bg-white text-sm ${fieldErrors.first_name ? "border-red-400 focus-visible:ring-2 focus-visible:ring-red-200" : "border-slate-300"}`}
                                    />
                                </div>
                                {fieldErrors.first_name && <p className="text-[11px] text-red-500 font-semibold mt-1 ml-1">{fieldErrors.first_name}</p>}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    Last Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="text"
                                        value={form.last_name}
                                        onChange={(e) => handleChange("last_name", e.target.value)}
                                        placeholder="Last name"
                                        className={`pl-9 pr-3 py-3 h-auto rounded-xl bg-white text-sm ${fieldErrors.last_name ? "border-red-400 focus-visible:ring-2 focus-visible:ring-red-200" : "border-slate-300"}`}
                                    />
                                </div>
                                {fieldErrors.last_name && <p className="text-[11px] text-red-500 font-semibold mt-1 ml-1">{fieldErrors.last_name}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    Citizenship <span className="text-red-500">*</span>
                                </label>
                                <SearchablePicklist
                                    options={countries}
                                    value={form.citizenship}
                                    onChange={(val) => handleChange("citizenship", val)}
                                    placeholder="Select citizenship"
                                    searchPlaceholder="Search countries..."
                                    className={fieldErrors.citizenship ? "border-red-400" : "border-slate-300 bg-white"}
                                />
                                {fieldErrors.citizenship && <p className="text-[11px] text-red-500 font-semibold mt-1 ml-1">{fieldErrors.citizenship}</p>}
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    Passport Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <CreditCard className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="text"
                                        value={form.passport_number}
                                        onChange={(e) => handleChange("passport_number", e.target.value)}
                                        placeholder="Passport number"
                                        className={`pl-10 pr-4 py-3 h-auto rounded-xl bg-white text-sm ${fieldErrors.passport_number ? "border-red-400 focus-visible:ring-2 focus-visible:ring-red-200" : "border-slate-300"}`}
                                    />
                                </div>
                                {fieldErrors.passport_number && <p className="text-[11px] text-red-500 font-semibold mt-1 ml-1">{fieldErrors.passport_number}</p>}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                    Applicant Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        type="email"
                                        value={form.applicant_email}
                                        onChange={(e) => handleChange("applicant_email", e.target.value)}
                                        placeholder="applicant@email.com"
                                        className={`pl-10 pr-4 py-3 h-auto rounded-xl bg-white text-sm ${fieldErrors.applicant_email ? "border-red-400 focus-visible:ring-2 focus-visible:ring-red-200" : "border-slate-300"}`}
                                    />
                                </div>
                                {fieldErrors.applicant_email && <p className="text-[11px] text-red-500 font-semibold mt-1 ml-1">{fieldErrors.applicant_email}</p>}
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="h-11 px-8 rounded-2xl font-semibold bg-[#001B44] hover:bg-[#002a6e]"
                            >
                                {isSubmitting ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Checking...</>
                                ) : "Next"}
                            </Button>
                        </div>
                    </form>
                </div>
            )}

            {step === 2 && conflicts.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
                    <div className="mb-5">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Step 2 of 2</p>
                        <h2 className="text-base font-bold text-slate-800">Existing Records Found</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            A record with matching passport or email already exists. Select one to continue.
                        </p>
                    </div>

                    <div className="flex items-start gap-3 p-3 mb-5 bg-amber-50 text-amber-700 rounded-xl border border-amber-100">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">Select an existing record to proceed.</span>
                    </div>

                    {error && (
                        <div className="flex items-start gap-3 p-3 mb-5 bg-red-50 text-red-700 rounded-xl border border-red-100">
                            <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-100">
                                    <th className="w-8 pb-3" />
                                    <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pb-3 pr-4">Full Name</th>
                                    <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pb-3 pr-4">Email</th>
                                    <th className="text-left text-[10px] font-black uppercase tracking-widest text-slate-400 pb-3">Passport</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {conflicts.map((c) => (
                                    <tr
                                        key={c.id}
                                        className={`cursor-pointer transition-colors ${selectedConflictId === c.id ? "bg-[#001B44]/5" : "hover:bg-slate-50"}`}
                                        onClick={() => { setSelectedConflictId(c.id); setError(null) }}
                                    >
                                        <td className="py-3.5 pr-3">
                                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${selectedConflictId === c.id ? "border-[#001B44] bg-[#001B44]" : "border-slate-300"}`}>
                                                {selectedConflictId === c.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                                            </div>
                                        </td>
                                        <td className="py-3.5 pr-4 font-semibold text-slate-700">{c.first_name} {c.last_name}</td>
                                        <td className="py-3.5 pr-4 text-slate-500">{c.email}</td>
                                        <td className="py-3.5 text-slate-500">{c.passport_number || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-between">
                        <Button variant="outline" onClick={handleBack} className="h-11 px-6 rounded-2xl font-semibold border-slate-200">
                            Previous
                        </Button>
                        <Button
                            onClick={handleGoToStudent}
                            disabled={!selectedConflictId}
                            className="h-11 px-8 rounded-2xl font-semibold bg-[#001B44] hover:bg-[#002a6e] disabled:opacity-40"
                        >
                            Go To Student
                        </Button>
                    </div>
                </div>
            )}

            {step === 2 && conflicts.length === 0 && createdMemberId && (
                <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm">
                    <div className="mb-5">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">Step 2 of 2</p>
                        <h2 className="text-base font-bold text-slate-800">Applicant Created</h2>
                        <p className="text-sm text-slate-500 mt-1">
                            The applicant has been successfully created
                            {user?.institution_name && <> for <span className="font-semibold text-slate-700">{user.institution_name}</span></>}.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 p-4 mb-6 bg-green-50 rounded-2xl border border-green-100">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div>
                            <p className="text-sm font-semibold text-green-800">{form.first_name} {form.last_name}</p>
                            <p className="text-xs text-green-600">{form.applicant_email}</p>
                        </div>
                    </div>

                    <div className="flex justify-between">
                        <Button variant="outline" onClick={handleBack} className="h-11 px-6 rounded-2xl font-semibold border-slate-200">
                            Previous
                        </Button>
                        <Button
                            onClick={handleGoToStudent}
                            className="h-11 px-8 rounded-2xl font-semibold bg-[#001B44] hover:bg-[#002a6e]"
                        >
                            Go To Student
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
